/**
 * Cross-panel "jump to this moment in the archive" helper.
 *
 * Selections park SELECTION_LEAD_MS before the moment of interest, and
 * camera_view seeks them to the nearest keyframe rather than decoding forward
 * to an exact position (it reads a fresh `seekNonce` as "this was a selection"
 * — see its apply-seek effect). The two go together: a keyframe seek lands at
 * or before its target, so without the lead an event could open already in
 * progress. With it, playback starts a beat early either way and the action
 * plays into view.
 *
 * Both helpers hand the pick to the shell's `selectCamera` (PanelContext)
 * rather than opening a panel themselves, so a moment clicked in a feed obeys
 * the same routing rule as a camera clicked in the tree. A moment always lands:
 * with several cameras on screen it focuses the one it names. They return the
 * action taken. Each names the scope it opens the view in (lib/viewScope.ts).
 *
 * Writing `{ cameraId, t }` on its own can't re-trigger a seek when the view is
 * already parked at that exact `t`: React sees identical props and the
 * apply-seek effect never re-runs. That silently drops common intents —
 * re-clicking the same event after playback has drifted past it, or selecting
 * two detections that share a frame's timestamp. The timeline doesn't have this
 * problem because it bumps a seek counter unconditionally. So the shell stamps
 * a `seekNonce` on every pick carrying a moment, giving the selection path
 * parity with the timeline. `seekNonce` is NOT in URL_PROP_KEYS, so it stays a
 * private prop and never leaks into the shareable URL.
 *
 * The nonce is `Date.now()` rather than a 0-based counter on purpose: it gets
 * persisted in localStorage alongside the panel's other private props,
 * and a per-load counter could regenerate a persisted value and get deduped on
 * the next session's first click. Consecutive human selections are always more
 * than a millisecond apart, so distinct intents never collide.
 */
import type { Bookmark } from '@/types/bookmarks';
import type {
    AddCameraCommand, CameraPickAction, CameraSelection, FocusCameraCommand, SelectCamera,
    ShowCameraCommand,
} from '@/types/panels';
import type { ViewScope } from '@/types/viewScope';

import { getBookmarkScope } from './viewScope';

// How far before the selected moment playback is parked.
export const SELECTION_LEAD_MS = 1000;

/**
 * How a moment is framed when the timeline is asked to zoom to it: the
 * moment's own span, padded either side, and never narrower than
 * MIN_TIMELINE_WINDOW_MS. The floor is the point of the helper — most
 * detections last well under a second, and a window their own length wide
 * shows the instant with nothing around it to read it against.
 */
export const MOMENT_PAD_MS = 5000;
export const MIN_TIMELINE_WINDOW_MS = 60_000;

export function getMomentWindow(
    startMs: number, endMs?: number | null, padMs: number = MOMENT_PAD_MS,
): { zoomStartMs: number; zoomEndMs: number } {
    let start = startMs - padMs;
    let end = Math.max(endMs ?? startMs, startMs) + padMs;
    const grow = (MIN_TIMELINE_WINDOW_MS - (end - start)) / 2;
    if (grow > 0) { start -= grow; end += grow; }
    return { zoomStartMs: start, zoomEndMs: end };
}

/**
 * The view panel's `command` prop: an instruction its scene carries out once.
 * `show` replaces whatever is on screen with the camera; `add` puts it beside
 * the rest, at `seekAtMs` if one is given. The nonce is what makes a repeat
 * of the same instruction a new one.
 */
export const showCameraCommand = (
    deviceId: string, alsoDeviceIds: string[] = [],
): ShowCameraCommand => ({
    kind: 'show',
    deviceId,
    ...(alsoDeviceIds.length ? { alsoDeviceIds } : {}),
    nonce: Date.now(),
});
export const addCameraCommand = (
    deviceId: string, seekAtMs: number | null = null, shouldJoin = false,
): AddCameraCommand => (
    { kind: 'add', deviceId, seekAtMs, shouldJoin, nonce: Date.now() });
// `focus` is the moment-carrying pick, handed to a scene of several cameras:
// the camera comes forward if it is already there, and the scene is left for it
// alone if it is not.
export const focusCameraCommand = (
    deviceId: string, seekAtMs: number | null = null,
): FocusCameraCommand => (
    { kind: 'focus', deviceId, seekAtMs, nonce: Date.now() });

/**
 * The private props that make a moment written straight to the view panel a
 * pick, as selectCamera stamps its own: the nonce seeks a repeated moment
 * again and has the timeline bring it on screen, and `pickCameraId` holds the
 * intent until the timeline shows the camera it names (null: the one on screen).
 */
export function getMomentPickProps(
    cameraId: string | null,
): { seekNonce: number; pickCameraId: string | null } {
    return {
        seekNonce: Date.now(),
        pickCameraId: cameraId,
    };
}

/** Open the view on a bookmark: it plays the bookmark and nothing else, from its first frame. */
export function openBookmark(selectCamera: SelectCamera, bookmark: Bookmark): void {
    const { zoomStartMs, zoomEndMs } = getMomentWindow(
        bookmark.startTimeMs, bookmark.startTimeMs + bookmark.durationMs);
    selectCamera(bookmark.deviceId, {
        seekAtMs: bookmark.startTimeMs,
        zoom: { startMs: zoomStartMs, endMs: zoomEndMs },
        scope: getBookmarkScope(bookmark),
    });
}

export function seekToCamera(
    selectCamera: SelectCamera,
    cameraId: string | undefined,
    momentMs: number | undefined,
    opts: CameraSelection,
): CameraPickAction | undefined | null {
    if (!cameraId || !momentMs) { return null; }
    return selectCamera(cameraId, { seekAtMs: momentMs - SELECTION_LEAD_MS, ...opts });
}

/**
 * "Frame this moment on the timeline" — like {@link seekToCamera} (park
 * playback at `seekMs`, less the selection lead) but ALSO zoom the timeline to
 * `[zoomStartMs, zoomEndMs]`. The window frames the moment itself and is NOT
 * lead-shifted — the lead only backs playback off the entry frame.
 *
 * The zoom rides its own `zoomNonce` rather than reusing `seekNonce`: plain
 * seeks (single-click on an object/event) must not re-zoom, and — because
 * openPanel merges private props into the panel's persisted local props — a
 * `zoomNonce` that only this call stamps lets the timeline ignore a stale
 * window left in the local props. The timeline additionally guards on the nonce's
 * wall-clock freshness so a remount (e.g. phone, where camera_view unmounts
 * when hidden) never replays an old window.
 *
 * A double-click and a context-menu command are instructions rather than
 * browse clicks, so this REPLACES a scene instead of being turned away by it.
 */
/** A window to frame, from {@link getMomentWindow} or objects' computeObjectTimeline. */
interface TimelineMoment {
    cameraId: string;
    seekMs: number;
    zoomStartMs: number;
    zoomEndMs: number;
}

export function showTimelineForMoment(
    selectCamera: SelectCamera,
    { cameraId, seekMs, zoomStartMs, zoomEndMs }: TimelineMoment,
    scope: ViewScope,
): CameraPickAction | undefined | null {
    if (!cameraId || !seekMs) { return null; }
    return selectCamera(cameraId, {
        seekAtMs: seekMs - SELECTION_LEAD_MS,
        shouldReplace: true,
        zoom: { startMs: zoomStartMs, endMs: zoomEndMs },
        scope,
    });
}
